import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiUrl } from '../../config/api';
import '../website-inquiries/websiteInquiries.css';
import './websiteChat.css';
import WebsiteChatConfig from './WebsiteChatConfig';
import MessageMedia from '../website-inquiries/MessageMedia';

function headers() {
  const session = JSON.parse(localStorage.getItem('sessionData') || '{}');
  return { Authorization: session.sessionKey || '', Username: session.username || '', 'Content-Type': 'application/json' };
}

function formatDate(value) {
  return value ? new Date(value).toLocaleString('es-CL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
}

function statusLabel(status) {
  return { open: 'Sin asignar', assigned: 'Asignada', closed: 'Cerrada' }[status] || status;
}

async function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('No se pudo leer la imagen.'));
    reader.readAsDataURL(file);
  });
}

export default function WebsiteChat() {
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [detail, setDetail] = useState(null);
  const [me, setMe] = useState(null);
  const [agents, setAgents] = useState([]);
  const [savedReplies, setSavedReplies] = useState([]);
  const [filter, setFilter] = useState('unassigned');
  const [brandFilter, setBrandFilter] = useState('all');
  const currentListFilters = useRef({ filter, brandFilter });
  currentListFilters.current = { filter, brandFilter };
  const listRequestRef = useRef(0);
  const listAbortRef = useRef(null);
  const [draft, setDraftValue] = useState('');
  const [internal, setInternalValue] = useState(false);
  // Text and visibility belong to one draft revision, including edits that return to the
  // original values while an earlier message or image is still being sent.
  const draftRevisionRef = useRef(0);
  const setDraft = useCallback((value) => {
    draftRevisionRef.current += 1;
    setDraftValue(value);
  }, []);
  const setInternal = useCallback((value) => {
    draftRevisionRef.current += 1;
    setInternalValue(value);
  }, []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [messageMenu, setMessageMenu] = useState(null);
  const [transfer, setTransfer] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const thread = useRef(null);
  const imageInput = useRef(null);
  // Only the latest detail request for the conversation that is still selected may fill the
  // panel: a slow answer (or poll) for the chat you just left must never appear under the one
  // you opened, because the composer would then answer the wrong customer.
  const detailRequestRef = useRef(0);
  const olderPagesRef = useRef(new Map());
  const deletedMessageIdsRef = useRef(new Map());
  const withoutDeleted = useCallback((id, messages) => messages.filter((message) => !deletedMessageIdsRef.current.get(String(id))?.has(String(message.id))), []);
  const detailAbortRef = useRef(null);
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;

  const detailMatchesSelection = Boolean(detail) && Boolean(selectedId) && String(detail.conversationId) === String(selectedId);
  const selected = useMemo(
    () => items.find((item) => String(item.id) === String(selectedId))
      || (detailMatchesSelection ? detail?.conversation : null)
      || null,
    [detail?.conversation, detailMatchesSelection, items, selectedId]
  );

  const json = useCallback(async (path, options = {}) => {
    const response = await fetch(apiUrl(path), { ...options, headers: headers() });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.success) throw new Error(data.message || 'No se pudo completar la acción.');
    return data;
  }, []);

  const loadBootstrap = useCallback(async () => {
    try {
      const [meData, agentsData] = await Promise.all([
        json('/website-chat/me'),
        json('/website-chat/agents'),
      ]);
      setMe(meData.user);
      setAgents((agentsData.agents || []).filter((agent) => agent.isActive));
    } catch (requestError) {
      setError(requestError.message);
    }
  }, [json]);

  const loadList = useCallback(async (quiet = false) => {
    const requestId = ++listRequestRef.current;
    listAbortRef.current?.abort();
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    listAbortRef.current = controller;
    const stillCurrent = () => requestId === listRequestRef.current
      && currentListFilters.current.filter === filter && currentListFilters.current.brandFilter === brandFilter;
    try {
      const data = await json(`/website-chat/conversations?filter=${encodeURIComponent(filter)}${brandFilter !== 'all' ? `&brand=${brandFilter}` : ''}`, controller ? { signal: controller.signal } : {});
      if (!stillCurrent()) return;
      setItems(data.conversations || []);
      setSelectedId((current) => {
        if (current && data.conversations?.some((item) => String(item.id) === String(current))) return current;
        return data.conversations?.[0]?.id || '';
      });
      if (!quiet) setError('');
    } catch (requestError) {
      if (requestError?.name !== 'AbortError' && stillCurrent() && !quiet) setError(requestError.message);
    } finally {
      if (listAbortRef.current === controller) listAbortRef.current = null;
    }
  }, [filter, brandFilter, json]);

  const loadDetail = useCallback(async (id, quiet = false) => {
    const requestId = detailRequestRef.current + 1;
    detailRequestRef.current = requestId;
    // A newer request supersedes any detail request still in flight.
    detailAbortRef.current?.abort();
    detailAbortRef.current = null;
    if (!id) { setDetail(null); return; }
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    detailAbortRef.current = controller;
    const stillCurrent = () => requestId === detailRequestRef.current && String(selectedIdRef.current) === String(id);
    try {
      const data = await json(`/website-chat/conversations/${id}`, controller ? { signal: controller.signal } : {});
      if (!stillCurrent()) return;
      const older = olderPagesRef.current.get(String(id));
      const messages = withoutDeleted(id, [...new Map([...(older?.messages || []), ...(data.messages || [])].map((message) => [message.id, message])).values()]).sort((a,b) => Number(a.id)-Number(b.id));
      setDetail({ ...data, messages, nextBeforeId: older ? older.cursor : data.nextBeforeId, conversationId: String(id) });
      if (!quiet) setError('');
    } catch (requestError) {
      if (requestError?.name === 'AbortError' || !stillCurrent()) return;
      if (!quiet) setError(requestError.message);
    } finally {
      if (detailAbortRef.current === controller) detailAbortRef.current = null;
    }
  }, [json, withoutDeleted]);

  useEffect(() => { loadBootstrap(); }, [loadBootstrap]);
  useEffect(() => {
    let ignore = false;
    json(`/website-chat/saved-replies${selected?.brandKey ? `?brand=${selected.brandKey}` : ''}`).then((data) => {
      if (!ignore) setSavedReplies((data.savedReplies || []).filter((reply) => reply.isActive));
    }).catch((requestError) => { if (!ignore) setError(requestError.message); });
    return () => { ignore = true; };
  }, [json, selected?.brandKey]);
  useEffect(() => {
    setSelectedId('');
    setDetail(null);
    loadList();
    const timer = window.setInterval(() => loadList(true), 5000);
    return () => {
      window.clearInterval(timer);
      listRequestRef.current += 1;
      listAbortRef.current?.abort();
    };
  }, [loadList]);
  useEffect(() => {
    // The thread, the draft and the send target always belong to the selected conversation.
    setDetail(null);
    setDraft('');
    setInternal(false);
    if (!selectedId) return undefined;
    loadDetail(selectedId);
    const timer = window.setInterval(() => loadDetail(selectedId, true), 4000);
    return () => {
      window.clearInterval(timer);
      // Leaving this conversation: invalidate and abort its pending detail/poll request.
      detailRequestRef.current += 1;
      detailAbortRef.current?.abort();
      detailAbortRef.current = null;
    };
  }, [loadDetail, selectedId, setDraft, setInternal]);
  useEffect(() => { thread.current?.scrollTo({ top: thread.current.scrollHeight }); }, [detail?.messages]);
  useEffect(() => {
    if (!detailMatchesSelection || !detail?.messages?.length) return undefined;
    const lastMessageId = detail.messages.at(-1).id;
    const acknowledgeVisible = () => {
      if (document.visibilityState === 'hidden') return;
      json(`/website-chat/conversations/${selectedId}/read`, { method: 'POST', body: JSON.stringify({ lastMessageId }) }).catch(() => { /* The next history poll retries acknowledgement. */ });
    };
    acknowledgeVisible(); document.addEventListener('visibilitychange', acknowledgeVisible);
    return () => document.removeEventListener('visibilitychange', acknowledgeVisible);
  }, [detail, detailMatchesSelection, json, selectedId]);
  useEffect(() => {
    if (!messageMenu) return undefined;
    const close = () => setMessageMenu(null);
    const closeOnEscape = (event) => { if (event.key === 'Escape') close(); };
    window.addEventListener('click', close);
    window.addEventListener('resize', close);
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('resize', close);
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [messageMenu]);

  async function refreshSelected() {
    // Refresh whichever conversation is open now (the operator may have moved on meanwhile).
    await Promise.all([loadDetail(selectedIdRef.current), loadList(true)]);
  }

  // The conversation whose thread is on screen; actions from the composer go only there.
  function visibleConversationId() {
    return detailMatchesSelection ? detail.conversationId : '';
  }

  async function claim() {
    setBusy(true);
    try {
      await json(`/website-chat/conversations/${selectedId}/claim`, { method: 'POST', body: '{}' });
      await refreshSelected();
    } catch (requestError) { setError(requestError.message); }
    finally { setBusy(false); }
  }

  // Clear text and visibility together only if the submitted draft is still untouched.
  function clearSentDraft(targetId, sentRevision) {
    if (String(selectedIdRef.current) !== String(targetId) || draftRevisionRef.current !== sentRevision) return;
    setDraft('');
    setInternal(false);
  }

  const sendInFlightRef = useRef(false);

  async function send() {
    const targetId = visibleConversationId();
    const sentText = draft.trim();
    if (!sentText || !targetId || sendInFlightRef.current) return;
    sendInFlightRef.current = true;
    const sentRevision = draftRevisionRef.current;
    setBusy(true);
    try {
      await json(`/website-chat/conversations/${targetId}/messages`, {
        method: 'POST', body: JSON.stringify({ message: sentText, internal }),
      });
      clearSentDraft(targetId, sentRevision);
      await refreshSelected();
    } catch (requestError) { setError(requestError.message); }
    finally { sendInFlightRef.current = false; setBusy(false); }
  }

  async function sendImage(file) {
    const targetId = visibleConversationId();
    if (!file || !targetId || sendInFlightRef.current) return;
    if (file.size > 5 * 1024 * 1024) { setError('La imagen debe pesar menos de 5 MB.'); return; }
    sendInFlightRef.current = true;
    const sentText = draft.trim();
    const sentRevision = draftRevisionRef.current;
    setBusy(true);
    try {
      const imageData = await fileToDataUrl(file);
      await json(`/website-chat/conversations/${targetId}/images`, {
        method: 'POST', body: JSON.stringify({ imageData, fileName: file.name, message: sentText, internal }),
      });
      clearSentDraft(targetId, sentRevision);
      await refreshSelected();
    } catch (requestError) { setError(requestError.message); }
    finally { sendInFlightRef.current = false; setBusy(false); if (imageInput.current) imageInput.current.value = ''; }
  }

  async function loadOlderMessages() {
    const targetId = visibleConversationId();
    if (!targetId || !detail?.nextBeforeId || busy) return;
    setBusy(true);
    try {
      const data = await json(`/website-chat/conversations/${targetId}?before=${detail.nextBeforeId}`);
      if (String(selectedIdRef.current) !== String(targetId)) return;
      const previous = olderPagesRef.current.get(String(targetId));
      const messages = withoutDeleted(targetId, [...(data.messages || []), ...(previous?.messages || [])]);
      olderPagesRef.current.set(String(targetId), { messages, cursor: data.nextBeforeId || null });
      setDetail((current) => ({ ...current, messages: [...new Map([...messages, ...(current?.messages || [])].map((message) => [message.id, message])).values()].sort((a,b) => Number(a.id)-Number(b.id)), nextBeforeId: data.nextBeforeId || null }));
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }

  async function setStatus(next) {
    setBusy(true);
    try {
      await json(`/website-chat/conversations/${selectedId}`, { method: 'PATCH', body: JSON.stringify({ status: next }) });
      await refreshSelected();
    } catch (requestError) { setError(requestError.message); }
    finally { setBusy(false); }
  }

  async function submitTransfer(event) {
    event.preventDefault();
    if (!transfer?.targetUsername || !transfer?.note?.trim()) return;
    setBusy(true);
    try {
      await json(`/website-chat/conversations/${selectedId}/transfer`, {
        method: 'POST', body: JSON.stringify(transfer),
      });
      setTransfer(null);
      await refreshSelected();
    } catch (requestError) { setError(requestError.message); }
    finally { setBusy(false); }
  }

  async function deleteMessage(message) {
    setMessageMenu(null);
    if (!window.confirm('¿Eliminar este mensaje permanentemente?')) return;
    try {
      const targetId = String(selectedId);
      await json(`/website-chat/conversations/${targetId}/messages/${message.id}`, { method: 'DELETE' });
      const deleted = deletedMessageIdsRef.current.get(targetId) || new Set();
      deleted.add(String(message.id));
      deletedMessageIdsRef.current.set(targetId, deleted);
      const older = olderPagesRef.current.get(targetId);
      if (older) olderPagesRef.current.set(targetId, { ...older, messages: withoutDeleted(targetId, older.messages) });
      setDetail((current) => String(current?.conversationId) === targetId
        ? { ...current, messages: withoutDeleted(targetId, current.messages || []) } : current);
      await refreshSelected();
    } catch (requestError) { setError(requestError.message); }
  }

  return <div className="site-messages site-messages--chat">
    <section className="site-messages__list">
      <div className="site-messages__list-top">
        <div><span className="site-messages__badge">Oliva y Miel · Tablee</span><h1>Conversaciones</h1></div>
        <button type="button" onClick={() => loadList()} title="Actualizar"><span className="material-symbols-outlined">refresh</span></button>
      </div>
      <label className="chat-brand-filter">Negocio<select aria-label="Filtrar conversaciones por negocio" value={brandFilter} onChange={(event) => setBrandFilter(event.target.value)}><option value="all">Todos los negocios</option>{(me?.brands || ['tablee', 'oliva']).includes('tablee') && <option value="tablee">Tablee</option>}{(me?.brands || ['tablee', 'oliva']).includes('oliva') && <option value="oliva">Oliva y Miel</option>}</select></label>
      <div className="chat-inbox-tabs">
        <button className={filter === 'unassigned' ? 'is-active' : ''} onClick={() => setFilter('unassigned')}>Sin asignar</button>
        <button className={filter === 'mine' ? 'is-active' : ''} onClick={() => setFilter('mine')}>Mis chats</button>
        <button className={filter === 'closed' ? 'is-active' : ''} onClick={() => setFilter('closed')}>Cerrados</button>
        {me?.isAdmin ? <button type="button" className="chat-inbox-settings" onClick={() => setSettingsOpen(true)} title="Configuración" aria-label="Abrir configuración del chat"><span className="material-symbols-outlined">settings</span></button> : null}
      </div>
      <div className="site-messages__items">
        {items.length ? items.map((item) => <button
          key={item.id}
          className={`site-messages__item ${String(selectedId) === String(item.id) ? 'is-active' : ''}`}
          onClick={() => setSelectedId(item.id)}
        >
          <span className={`site-messages__dot ${item.unreadCount ? 'is-unread' : ''}`} />
          <span className="site-messages__item-main"><strong>{item.visitorName}</strong><small className={`chat-brand-label is-${item.brandKey || 'tablee'}`}>{item.brandKey === 'oliva' ? 'Oliva y Miel' : 'Tablee'}</small><small>{item.lastMessagePreview || 'Nueva conversación'}</small></span>
          <span className="site-messages__item-meta">{item.unreadCount ? <span>{item.unreadCount}</span> : null}<small>{formatDate(item.lastMessageAt)}</small></span>
        </button>) : <p className="chat-inbox-empty">No hay conversaciones en esta bandeja.</p>}
      </div>
    </section>

    <section className="site-messages__detail">
      {error ? <div className="site-messages__error">{error}<button type="button" onClick={() => setError('')}>×</button></div> : null}
      {!selected || !detailMatchesSelection ? <div className="site-messages__placeholder">Selecciona una conversación.</div> : <>
        <div className="site-messages__contact">
          <div><span className={`site-messages__badge is-${selected.status}`}>{statusLabel(selected.status)}</span><h2>{selected.visitorName}</h2><p>{selected.visitorEmail || 'Sin correo'} {selected.visitorPhone ? `· ${selected.visitorPhone}` : ''}</p></div>
          <div className="site-messages__actions">
            {selected.status === 'open' ? <button type="button" disabled={busy} onClick={claim}>Tomar conversación</button> : null}
            {selected.status === 'assigned' ? <button type="button" disabled={busy} onClick={() => setTransfer({ targetUsername: '', note: '' })}><span className="material-symbols-outlined">swap_horiz</span>Transferir</button> : null}
            {selected.status !== 'closed' ? <button type="button" disabled={busy} onClick={() => setStatus('closed')}><span className="material-symbols-outlined">archive</span>Cerrar</button> : <button type="button" disabled={busy} onClick={() => setStatus('assigned')}>Reabrir</button>}
          </div>
        </div>
        <div className="site-messages__meta"><span className={`chat-brand-label is-${selected.brandKey || 'tablee'}`}>{selected.brandKey === 'oliva' ? 'Oliva y Miel' : 'Tablee'}</span><span>Chat web</span><span>{selected.assignedToUsername || 'Sin asignar'}</span><span>{selected.source}</span></div>
        {(selected.productContext?.name || selected.cartContext?.length) ? <aside className="chat-context">
          {selected.productContext?.name ? <div><strong>Producto consultado</strong><span>{selected.productContext.name}</span>{selected.pageUrl ? <a href={selected.pageUrl} target="_blank" rel="noreferrer">Abrir página</a> : null}</div> : null}
          {selected.cartContext?.length ? <div><strong>Carro al iniciar</strong><span>{selected.cartContext.map((item) => `${item.quantity} × ${item.name}`).join(' · ')}</span></div> : null}
        </aside> : null}
        <div className="site-messages__thread" ref={thread}>{detail.nextBeforeId && <button type="button" disabled={busy} onClick={loadOlderMessages}>Ver mensajes anteriores</button>}{detail.messages.map((message) => <article
          key={message.id}
          onContextMenu={(event) => { if (!me?.isAdmin) return; event.preventDefault(); setMessageMenu({ message, x: event.clientX, y: event.clientY }); }}
          className={`site-messages__bubble site-messages__bubble--${message.senderType === 'staff' ? 'outbound' : message.senderType}`}
        >
          <div><strong>{message.senderName || message.senderType}</strong><small>{formatDate(message.createdAt)}</small></div>
          {message.attachment ? <MessageMedia attachment={{ type: 'image', url: message.attachment.staffUrl, fileName: message.attachment.fileName }} headers={headers} /> : null}
          {message.bodyText && message.bodyText !== 'Imagen' ? <p>{message.bodyText}</p> : null}
        </article>)}</div>
        {detail.notifications?.length > 0 && <aside className="site-messages__error" role="status">{detail.notifications.map(job => <p key={job.id}>{job.kind === 'email' ? 'Aviso por correo' : 'Notificación móvil'}: {job.status === 'failed' ? 'Se reintentará la entrega' : 'Pendiente de entrega'}{job.status === 'failed' && <button type="button" onClick={async () => { await json(`/website-chat/conversations/${selectedId}/notifications/${job.id}/retry`, { method: 'POST', body: '{}' }); await refreshSelected(); }}>Reintentar aviso</button>}</p>)}</aside>}
        <div className="site-messages__composer">
          <label className={`chat-inbox-note ${internal ? 'is-internal' : ''}`}>
            <input type="checkbox" checked={internal} onChange={(event) => setInternal(event.target.checked)} />
            <span className="chat-inbox-note__slider" aria-hidden="true"><span /></span>
            <span className="chat-inbox-note__label">{internal ? 'Nota interna' : 'Respuesta al cliente'}</span>
          </label>
          <div className="chat-saved-replies">
            <select value="" onChange={(event) => setDraft(event.target.value)} aria-label="Respuesta guardada">
              <option value="">Usar respuesta guardada…</option>
              {savedReplies.map((reply) => <option key={reply.id} value={reply.bodyText}>{reply.title}</option>)}
            </select>
          </div>
          <div className="chat-inbox-input">
            <textarea value={draft} onChange={(event) => setDraft(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); send(); } }} placeholder={internal ? 'Nota visible solo para el equipo…' : 'Escribe una respuesta…'} />
            <input ref={imageInput} type="file" accept="image/jpeg,image/png,image/webp,image/gif" hidden onChange={(event) => sendImage(event.target.files?.[0])} />
            <button type="button" className="chat-image-button" onClick={() => imageInput.current?.click()} disabled={busy} aria-label="Adjuntar imagen"><span className="material-symbols-outlined">image</span></button>
            <button type="button" className="chat-send-button" onClick={send} disabled={busy || !draft.trim()} aria-label={internal ? 'Guardar nota interna' : 'Enviar respuesta'}><span className="material-symbols-outlined">send</span></button>
          </div>
        </div>
      </>}
    </section>

    {transfer ? <div className="chat-modal-backdrop" onMouseDown={() => setTransfer(null)}>
      <form className="chat-modal" onSubmit={submitTransfer} onMouseDown={(event) => event.stopPropagation()}>
        <header><div><span>Asignación</span><h2>Transferir conversación</h2></div><button type="button" onClick={() => setTransfer(null)}>×</button></header>
        <label>Agente receptor<select required value={transfer.targetUsername} onChange={(event) => setTransfer({ ...transfer, targetUsername: event.target.value })}><option value="">Seleccionar agente</option>{agents.filter((agent) => agent.username !== selected?.assignedToUsername && (agent.brands || ['tablee']).includes(selected?.brandKey || 'tablee')).map((agent) => <option key={agent.username} value={agent.username}>{agent.name || agent.username}</option>)}</select></label>
        <label>Nota interna<textarea required value={transfer.note} onChange={(event) => setTransfer({ ...transfer, note: event.target.value })} placeholder="Contexto para quien recibe…" /></label>
        <footer><button type="button" onClick={() => setTransfer(null)}>Cancelar</button><button type="submit" disabled={busy}>Transferir y notificar</button></footer>
      </form>
    </div> : null}

    {messageMenu ? <div className="chat-message-menu" style={{ left: Math.min(messageMenu.x, window.innerWidth - 210), top: Math.min(messageMenu.y, window.innerHeight - 70) }} onClick={(event) => event.stopPropagation()} role="menu"><button type="button" onClick={() => deleteMessage(messageMenu.message)} role="menuitem"><span className="material-symbols-outlined">delete</span>Eliminar mensaje</button></div> : null}
    {settingsOpen ? <WebsiteChatConfig initialBrand={selected?.brandKey || (brandFilter === 'oliva' ? 'oliva' : 'tablee')} onClose={() => setSettingsOpen(false)} /> : null}
  </div>;
}
